import { useSelector } from "react-redux";

import { getSippeers } from "@/api/stackApi";
import Avatar from "@/design/components/Avatar";
import formatDuration from "@/utils/formaty";
import Valkyrie, { viArrowRightFromBracket, viArrowRightToBracket, viList } from "@sippy-platform/valkyrie";

import { Stack, Tooltip, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";

import DataCell from "../_Components/DataCell";
import TableRow from "../_Components/TableRow";

export default function QueueLogItem({
  queueEvent,
}: {
  queueEvent: {
    date: string;
    duration: string;
    secondsRinging: number;
    secondsInQueue: number;
    queueName: string;
    queueExtension: string;
    agent?: string;
    callerId?: string;
    positionWhenLeft?: number;
    initialPosition?: number;
  };
}) {
  const pbx = useSelector((state) => state.domain.pbx);

  const sippeersQuery = useQuery({
    queryKey: [pbx.context, "sippeers"],
    queryFn: getSippeers,
    refetchInterval: 10 * 1000 * 60,
  });

  const user = sippeersQuery.data?.find((sippeer: { username: string }) => sippeer.username === queueEvent?.agent);

  // Data
  const eventTypes = [
    { event: "ABANDON", label: "Abandoned" },
    { event: "ATTENDEDTRANSFER", label: "Attended transfer" },
    { event: "BLINDTRANSFER", label: "Blind transfer" },
    { event: "COMPLETEAGENT", label: "Complete agent" },
    { event: "COMPLETECALLER", label: "Complete caller" },
    { event: "CONNECT", label: "Connect" },
    { event: "ENTERQUEUE", label: "Enter queue" },
    { event: "EXITEMPTY", label: "Exit empty" },
    { event: "EXITWITHTIMEOUT", label: "Exit with timeout" },
    { event: "RINGCANCELED", label: "Ring canceled" },
    { event: "RINGNOANSWER", label: "Ring no answer" },
    { event: "TRANSFER", label: "Transfer" },
  ];

  return (
    <TableRow>
      <DataCell
        invertOrder
        noWrap
        tabular
        primary={format(queueEvent.date, "pp")}
        secondary={format(queueEvent.date, "PPP")}
      />
      <DataCell
        tabular
        align="right"
        noWrap
        primary={
          queueEvent.duration
            ? formatDuration(queueEvent.duration)
            : queueEvent.secondsRinging
              ? formatDuration(queueEvent.secondsRinging)
              : queueEvent.secondsInQueue
                ? formatDuration(queueEvent.secondsInQueue)
                : ""
        }
      />
      <DataCell primary={queueEvent.callerId} />
      <Stack
        direction="row"
        sx={{
          flexWrap: "nowrap",
          alignItems: "center",
          gap: 2,
        }}
      >
        {queueEvent.initialPosition && (
          <Tooltip title="Initial position">
            <Typography
              sx={{
                fontSize: 14,
                lineHeight: 1.125,
                fontVariantNumeric: "tabular-nums",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Valkyrie icon={viArrowRightToBracket} /> {queueEvent.initialPosition}
            </Typography>
          </Tooltip>
        )}
        {queueEvent.positionWhenLeft && (
          <Tooltip title="Position on leave">
            <Typography
              sx={{
                fontSize: 14,
                lineHeight: 1.125,
                fontVariantNumeric: "tabular-nums",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Valkyrie icon={viArrowRightFromBracket} /> {queueEvent.positionWhenLeft}
            </Typography>
          </Tooltip>
        )}
      </Stack>
      <DataCell
        startDecorator={<Avatar text={queueEvent.queueName} px={24} icon={viList} />}
        primary={queueEvent.queueName}
        secondary={queueEvent.queueExtension}
      />
      {user ? (
        <DataCell startDecorator={<Avatar text={user.name} px={24} />} primary={user.name} secondary={user.exten} />
      ) : (
        <DataCell
          primary={queueEvent.agent?.split("-").length === 3 ? queueEvent.agent?.split("-")[2] : queueEvent.agent}
          secondary={queueEvent.agent?.split("-").length === 3 ? "Deleted" : undefined}
        />
      )}
      <DataCell
        primary={eventTypes.find((eventType) => eventType.event === queueEvent.event)?.label ?? queueEvent.event}
      />
    </TableRow>
  );
}
